/**
 * Assault encounter director — irregular drip waves + brute packs,
 * then retreat → boss drop → arm combat. Phaser-free.
 * Boss is dormant (off-map / unarmed) until bossCombatArmed.
 */

import { raidCatalog } from "../../data/raid-catalog";
import {
  tryEmitAssaultChunkBark,
  tryEmitAddCombatBark,
  tryEmitBrutePackSpawnBark,
} from "./raid-bark-emit";
import { RaidEvent, type RaidSimEvent } from "./raid-events";
import { raidRandom } from "./raid-rng";
import type {
  RaidBattleState,
  RaidFormationGoal,
  RaidUnit,
} from "./raid-types";
import {
  RaidEnemyTag,
  enemyHasTag,
} from "./raid-enemy";
import {
  beginEscalateScope,
  clearEscalateScope,
} from "./raid-escalate";

export type AssaultScriptId = keyof typeof raidCatalog.assault.scripts;
export type AssaultScript = (typeof raidCatalog.assault.scripts)[AssaultScriptId];

export type AssaultSpawnFns = {
  spawnNorthWallAdds: (state: RaidBattleState, count: number) => RaidUnit[];
  spawnSouthWallAdds: (state: RaidBattleState, count: number) => RaidUnit[];
  spawnRingAdds: (state: RaidBattleState, count: number) => RaidUnit[];
  spawnNorthWallChargers: (state: RaidBattleState, count: number) => RaidUnit[];
  spawnSouthWallChargers: (state: RaidBattleState, count: number) => RaidUnit[];
  spawnRingChargers: (state: RaidBattleState, count: number) => RaidUnit[];
  makeBrute: (state: RaidBattleState, x: number, y: number) => RaidUnit;
  makeCharger: (state: RaidBattleState, x: number, y: number) => RaidUnit;
  clampToPlayBounds: (
    x: number,
    y: number,
    pad?: number,
  ) => { x: number; y: number };
  playBounds: () => {
    xMin: number;
    xMax: number;
    yMin: number;
    yMax: number;
  };
};

export function assaultScript(scriptId?: string): AssaultScript {
  const id = (scriptId ??
    raidCatalog.assault.demoScriptId) as AssaultScriptId;
  const s = raidCatalog.assault.scripts[id];
  if (!s) throw new Error(`Unknown assault script: ${String(id)}`);
  return s;
}

export function isAssaultMode(state: RaidBattleState): boolean {
  return state.encounter.mode === "assault";
}

export function isBossEncounterActive(state: RaidBattleState): boolean {
  return (
    state.encounter.mode === "boss" && state.encounter.bossCombatArmed
  );
}

/** Boss skipped for combat/HUD until assault+transition finish and armed. */
export function isBossDormant(state: RaidBattleState): boolean {
  const enc = state.encounter;
  return (
    enc.mode === "assault" ||
    enc.mode === "transition" ||
    (enc.mode === "boss" && !enc.bossCombatArmed)
  );
}

/** Living packChew swarm (not elite brutes). */
function livingThralls(state: RaidBattleState): RaidUnit[] {
  return state.units.filter(
    (u) =>
      u.team === 1 &&
      u.hp > 0 &&
      enemyHasTag(u, RaidEnemyTag.PackChew),
  );
}

/** Living assault brutes (hp>0), armed or still dropping. */
function livingBrutes(state: RaidBattleState): RaidUnit[] {
  return state.units.filter(
    (u) =>
      u.team === 1 &&
      u.hp > 0 &&
      enemyHasTag(u, RaidEnemyTag.Elite),
  );
}

/**
 * Living non-elite charge casters (mid-drip / with-brute / boss chargers).
 * Tags: chargeCaster && !elite — Soft≪AA-safe; judgment untouched.
 */
function livingChargers(state: RaidBattleState): RaidUnit[] {
  return state.units.filter(
    (u) =>
      u.team === 1 &&
      u.hp > 0 &&
      enemyHasTag(u, RaidEnemyTag.ChargeCaster) &&
      !enemyHasTag(u, RaidEnemyTag.Elite),
  );
}

/** Must be dead before spawning the wave's brute pack (thralls + mid-drip chargers). */
function livingPreBruteGate(state: RaidBattleState): RaidUnit[] {
  return state.units.filter((u) => {
    if (u.team !== 1 || u.hp <= 0) return false;
    if (enemyHasTag(u, RaidEnemyTag.PackChew)) return true;
    return (
      enemyHasTag(u, RaidEnemyTag.ChargeCaster) &&
      !enemyHasTag(u, RaidEnemyTag.Elite)
    );
  });
}

function push(state: RaidBattleState, ev: RaidSimEvent): void {
  state.events.push(ev);
}

function randRange(state: RaidBattleState, min: number, max: number): number {
  if (max <= min) return min;
  return min + raidRandom(state) * (max - min);
}

function randIntInclusive(
  state: RaidBattleState,
  min: number,
  max: number,
): number {
  const lo = Math.floor(min);
  const hi = Math.floor(max);
  if (hi <= lo) return lo;
  return lo + Math.floor(raidRandom(state) * (hi - lo + 1));
}

/** Park boss off-map for assault (no body-sep / no tickBoss). */
export function parkBossDormant(
  state: RaidBattleState,
  script?: AssaultScript,
): void {
  const s = script ?? assaultScript(state.encounter.scriptId);
  const d = s.dormantBoss;
  state.boss.x = d.x;
  state.boss.y = d.y;
  state.boss.vx = 0;
  state.boss.vy = 0;
  state.boss.intentVx = 0;
  state.boss.intentVy = 0;
  state.boss.telegraph = null;
  state.boss.leap = null;
  state.boss.stunnedUntil = Number.POSITIVE_INFINITY;
}

/**
 * Begin assault encounter (call after createBattleState for live play).
 * Existing tests that omit this stay in boss mode.
 */
export function startAssaultEncounter(
  state: RaidBattleState,
  scriptId?: string,
): void {
  const id = scriptId ?? raidCatalog.assault.demoScriptId;
  const script = assaultScript(id);
  state.encounter = {
    mode: "assault",
    scriptId: id,
    waveIndex: 0,
    waveSpawned: false,
    toSpawn: 0,
    toSpawnSouth: 0,
    nextChunkIn: 0,
    bruteSpawnedForWave: false,
    chargerMidDripFiredMask: 0,
    chargerSpawnedForWave: false,
    delayRemain: script.waves[0]?.delaySec ?? 0,
    transitionRemain: 0,
    transitionPhase: null,
    bossCombatArmed: false,
    formationGoals: null,
    addBarkCdLeft: 0,
    escalateAnchorTime: -1,
  };
  parkBossDormant(state, script);
  push(state, {
    kind: RaidEvent.Encounter,
    mode: "assault",
    wave: 0,
    bruteIndex: 0,
    messageKey: "raid.msg.assaultStart",
  });
}

/**
 * Jump to a wave index after {@link startAssaultEncounter} (Sim Lab / diag).
 * Clears living team-1, resets drip flags, uses that wave's delaySec.
 */
export function seekAssaultWave(
  state: RaidBattleState,
  waveIndex: number,
): void {
  if (state.encounter.mode !== "assault") {
    throw new Error("seekAssaultWave requires assault mode");
  }
  const script = assaultScript(state.encounter.scriptId);
  const idx = Math.max(0, Math.min(script.waves.length - 1, Math.floor(waveIndex)));
  for (const u of state.units) {
    if (u.team === 1) u.hp = 0;
  }
  state.encounter.waveIndex = idx;
  state.encounter.waveSpawned = false;
  state.encounter.toSpawn = 0;
  state.encounter.toSpawnSouth = 0;
  state.encounter.nextChunkIn = 0;
  state.encounter.bruteSpawnedForWave = false;
  state.encounter.chargerMidDripFiredMask = 0;
  state.encounter.chargerSpawnedForWave = false;
  state.encounter.delayRemain = script.waves[idx]?.delaySec ?? 0;
  clearEscalateScope(state);
}

type AssaultWave = AssaultScript["waves"][number];

/** Wave thrall count that must spawn from the south wall (0 = north-only). */
export function waveSouthQuota(wave: AssaultWave): number {
  const raw = Number(wave.southFrac ?? 0);
  const frac = Number.isFinite(raw) ? Math.max(0, Math.min(1, raw)) : 0;
  return Math.round(wave.count * frac);
}

/**
 * How many of this drip chunk should come from the south wall so remaining
 * north/south quotas stay feasible.
 */
export function allocateSouthChunk(
  chunk: number,
  toSpawn: number,
  toSpawnSouth: number,
): number {
  if (chunk <= 0 || toSpawn <= 0 || toSpawnSouth <= 0) return 0;
  const southRemain = Math.min(toSpawnSouth, toSpawn);
  const northRemain = toSpawn - southRemain;
  let south = Math.round(chunk * (southRemain / toSpawn));
  south = Math.max(0, Math.min(chunk, southRemain, south));
  if (chunk - south > northRemain) {
    south = chunk - northRemain;
  }
  const after = toSpawn - chunk;
  if (southRemain - south > after) {
    south = southRemain - after;
  }
  return Math.max(0, Math.min(chunk, southRemain, south));
}

/**
 * Scale table gaps so expected drip duration ≈ streamSec.
 * Irregular chunk/gap ratios are preserved; streamSec is the pacing budget.
 */
export function dripGapScale(wave: AssaultWave): number {
  const avgChunk = Math.max(1, (wave.chunkMin + wave.chunkMax) * 0.5);
  const nChunks = Math.max(1, Math.ceil(wave.count / avgChunk));
  const nGaps = Math.max(1, nChunks - 1);
  const avgGap = (wave.gapMinSec + wave.gapMaxSec) * 0.5;
  if (wave.streamSec <= 1e-9 || avgGap <= 1e-9) return 1;
  return wave.streamSec / (nGaps * avgGap);
}

function nextDripGap(state: RaidBattleState, wave: AssaultWave): number {
  return (
    randRange(state, wave.gapMinSec, wave.gapMaxSec) * dripGapScale(wave)
  );
}

function beginWaveDrip(
  state: RaidBattleState,
  script: AssaultScript,
  waveIndex: number,
): void {
  const wave = script.waves[waveIndex];
  if (!wave) return;
  state.encounter.waveSpawned = true;
  state.encounter.toSpawn = wave.count;
  state.encounter.toSpawnSouth = waveSouthQuota(wave);
  state.encounter.nextChunkIn = 0;
  state.encounter.bruteSpawnedForWave = false;
  state.encounter.chargerMidDripFiredMask = 0;
  state.encounter.chargerSpawnedForWave = false;
  // Fresh stage clock — prior wave stacks never carry forward.
  beginEscalateScope(state);
  push(state, {
    kind: RaidEvent.Encounter,
    mode: "assault",
    wave: waveIndex + 1,
    bruteIndex: state.encounter.waveIndex,
    messageKey: "raid.msg.assaultWave",
  });
}

function spawnDripChunk(
  state: RaidBattleState,
  wave: AssaultWave,
  chunk: number,
  fns: AssaultSpawnFns,
): RaidUnit[] {
  if (chunk <= 0) return [];
  const kind = String(wave.spawnKind);
  if (kind === "ring") {
    return fns.spawnRingAdds(state, chunk);
  }
  if (kind === "southWall") {
    return fns.spawnSouthWallAdds(state, chunk);
  }
  // northWall (default): optional southFrac split across the drip.
  const southN = allocateSouthChunk(
    chunk,
    state.encounter.toSpawn,
    state.encounter.toSpawnSouth,
  );
  const northN = chunk - southN;
  const spawned: RaidUnit[] = [];
  if (northN > 0) {
    spawned.push(...fns.spawnNorthWallAdds(state, northN));
  }
  if (southN > 0) {
    spawned.push(...fns.spawnSouthWallAdds(state, southN));
  }
  state.encounter.toSpawnSouth = Math.max(
    0,
    state.encounter.toSpawnSouth - southN,
  );
  return spawned;
}

function tickWaveDrip(
  state: RaidBattleState,
  script: AssaultScript,
  waveIndex: number,
  dt: number,
  fns: AssaultSpawnFns,
): void {
  const wave = script.waves[waveIndex];
  if (!wave || state.encounter.toSpawn <= 0) return;

  state.encounter.nextChunkIn -= dt;
  if (state.encounter.nextChunkIn > 1e-6) return;

  const chunk = Math.min(
    state.encounter.toSpawn,
    randIntInclusive(state, wave.chunkMin, wave.chunkMax),
  );
  if (chunk <= 0) {
    state.encounter.toSpawn = 0;
    state.encounter.toSpawnSouth = 0;
    return;
  }
  const spawned = spawnDripChunk(state, wave, chunk, fns);
  tryEmitAssaultChunkBark(state, spawned);
  state.encounter.toSpawn -= chunk;
  if (state.encounter.toSpawn <= 0) {
    state.encounter.toSpawn = 0;
    state.encounter.toSpawnSouth = 0;
    state.encounter.nextChunkIn = 0;
  } else {
    state.encounter.nextChunkIn = nextDripGap(state, wave);
  }
}

function bruteCountForWave(script: AssaultScript, waveIndex: number): number {
  const row = script.brutes.find((b) => b.afterWave === waveIndex);
  if (!row) return 0;
  // Missing count historically meant "one elite"; treat absent/invalid as 1.
  const raw = row.count;
  if (raw == null || !Number.isFinite(Number(raw))) return 1;
  return Math.max(0, Math.floor(Number(raw)));
}

function chargerRowsForWave(
  script: AssaultScript,
  waveIndex: number,
): readonly { afterWave: number; count?: number; atDripFrac?: number }[] {
  const rows = script.chargers ?? [];
  return rows.filter((c) => c.afterWave === waveIndex);
}

/** Mid-drip rows: finite atDripFrac in (0,1], sorted ascending by frac. */
function midDripChargerRows(
  script: AssaultScript,
  waveIndex: number,
): { count: number; atDripFrac: number }[] {
  const out: { count: number; atDripFrac: number }[] = [];
  for (const row of chargerRowsForWave(script, waveIndex)) {
    const frac = Number(row.atDripFrac);
    if (!Number.isFinite(frac) || frac <= 0) continue;
    const raw = row.count;
    const count =
      raw == null || !Number.isFinite(Number(raw))
        ? 0
        : Math.max(0, Math.floor(Number(raw)));
    if (count <= 0) continue;
    out.push({ count, atDripFrac: Math.min(1, frac) });
  }
  out.sort((a, b) => a.atDripFrac - b.atDripFrac);
  return out;
}

/** With-brute-pack count: rows without atDripFrac (legacy; demo uses mid-drip only). */
function withBruteChargerCount(
  script: AssaultScript,
  waveIndex: number,
): number {
  let n = 0;
  for (const row of chargerRowsForWave(script, waveIndex)) {
    const frac = Number(row.atDripFrac);
    if (Number.isFinite(frac) && frac > 0) continue;
    const raw = row.count;
    if (raw == null || !Number.isFinite(Number(raw))) continue;
    n += Math.max(0, Math.floor(Number(raw)));
  }
  return n;
}

/**
 * Mid-drip / with-brute charger pack — same spawnKind / southFrac regions as
 * that wave's thrall drip (northWall / southWall / ring).
 */
function spawnChargerPack(
  state: RaidBattleState,
  script: AssaultScript,
  fns: AssaultSpawnFns,
  count: number,
  wave: AssaultWave | undefined,
): void {
  if (count <= 0) return;
  const kind = wave ? String(wave.spawnKind) : "northWall";
  if (kind === "ring") {
    fns.spawnRingChargers(state, count);
  } else if (kind === "southWall") {
    fns.spawnSouthWallChargers(state, count);
  } else {
    // northWall (default): optional southFrac split like thrall drip.
    const raw = Number(wave?.southFrac ?? 0);
    const frac = Number.isFinite(raw) ? Math.max(0, Math.min(1, raw)) : 0;
    const southN = Math.round(count * frac);
    const northN = count - southN;
    if (northN > 0) fns.spawnNorthWallChargers(state, northN);
    if (southN > 0) fns.spawnSouthWallChargers(state, southN);
  }
  push(state, {
    kind: RaidEvent.Encounter,
    mode: "assault",
    wave: state.encounter.waveIndex + 1,
    bruteIndex: state.encounter.waveIndex + 1,
    messageKey: "raid.msg.assaultCharger",
  });
}

/**
 * Fire each mid-drip charger row once when thrall drip ≥ atDripFrac.
 * Bitmask tracks fired rows so W3 can spawn 2 then 2 (not ×4 at once).
 */
function tryMidDripChargers(
  state: RaidBattleState,
  script: AssaultScript,
  waveIndex: number,
  fns: AssaultSpawnFns,
): void {
  // Already fully resolved (all mid-drip rows + with-brute slot).
  if (state.encounter.chargerSpawnedForWave) return;
  const rows = midDripChargerRows(script, waveIndex);
  if (rows.length === 0) return;
  const wave = script.waves[waveIndex];
  if (!wave || wave.count <= 0) return;
  const spawnedFrac =
    (wave.count - state.encounter.toSpawn) / wave.count;
  let mask = state.encounter.chargerMidDripFiredMask | 0;
  let changed = false;
  for (let i = 0; i < rows.length; i++) {
    const bit = 1 << i;
    if ((mask & bit) !== 0) continue;
    const row = rows[i]!;
    if (spawnedFrac + 1e-9 < row.atDripFrac) continue;
    spawnChargerPack(state, script, fns, row.count, wave);
    mask |= bit;
    changed = true;
  }
  if (changed) state.encounter.chargerMidDripFiredMask = mask;
  const allMid =
    rows.length > 0 && mask === (1 << rows.length) - 1;
  if (allMid && withBruteChargerCount(script, waveIndex) === 0) {
    state.encounter.chargerSpawnedForWave = true;
  }
}

function spawnChargersWithBrutes(
  state: RaidBattleState,
  script: AssaultScript,
  fns: AssaultSpawnFns,
): void {
  const count = withBruteChargerCount(script, state.encounter.waveIndex);
  // With-brute slot resolves here (count may be 0) — marks wave chargers done.
  state.encounter.chargerSpawnedForWave = true;
  const wave = script.waves[state.encounter.waveIndex];
  spawnChargerPack(state, script, fns, count, wave);
}

function spawnBruteForWave(
  state: RaidBattleState,
  script: AssaultScript,
  fns: AssaultSpawnFns,
): void {
  const count = bruteCountForWave(script, state.encounter.waveIndex);
  const b = fns.playBounds();
  const cx = (b.xMin + b.xMax) * 0.5;
  const y = b.yMin + 80;
  const gap = 56;
  const startX = cx - ((count - 1) * gap) * 0.5;
  const spawned: RaidUnit[] = [];
  for (let i = 0; i < count; i++) {
    const pos = fns.clampToPlayBounds(
      startX + i * gap,
      y,
      script.bruteStats.radius,
    );
    const u = fns.makeBrute(state, pos.x, pos.y);
    state.units.push(u);
    spawned.push(u);
    push(state, {
      kind: RaidEvent.Spawn,
      unitId: u.id,
      x: u.x,
      y: u.y,
      team: 1,
    });
  }
  tryEmitBrutePackSpawnBark(state, spawned);
  state.encounter.bruteSpawnedForWave = true;
  // With-brute charger rows (e.g. W3×4). Mid-drip rows already fired earlier.
  spawnChargersWithBrutes(state, script, fns);
  push(state, {
    kind: RaidEvent.Encounter,
    mode: "assault",
    wave: state.encounter.waveIndex + 1,
    bruteIndex: state.encounter.waveIndex + 1,
    messageKey: "raid.msg.assaultBrute",
  });
}

function assignFormationGoals(
  state: RaidBattleState,
  script: AssaultScript,
  fns: AssaultSpawnFns,
): void {
  const allies = state.units
    .filter((u) => u.team === 0 && u.hp > 0)
    .sort((a, b) => a.x - b.x);
  const gap = script.transition.formationSlotGap;
  const n = allies.length;
  const b = fns.playBounds();
  const cx = (b.xMin + b.xMax) * 0.5;
  const startX = cx - ((n - 1) * gap) * 0.5;
  const goals: RaidFormationGoal[] = [];
  for (let i = 0; i < n; i++) {
    const u = allies[i]!;
    const p = fns.clampToPlayBounds(
      startX + i * gap,
      script.transition.formationAllyY,
      u.radius,
    );
    goals.push({ unitId: u.id, x: p.x, y: p.y });
  }
  state.encounter.formationGoals = goals;
}

function beginTransition(state: RaidBattleState, script: AssaultScript, fns: AssaultSpawnFns): void {
  state.encounter.mode = "transition";
  state.encounter.transitionPhase = "retreat";
  state.encounter.transitionRemain = script.transition.retreatSec;
  state.encounter.bossCombatArmed = false;
  // Drop assault stacks before Colossus — boss gets its own clock on arm.
  clearEscalateScope(state);
  assignFormationGoals(state, script, fns);
  push(state, {
    kind: RaidEvent.Encounter,
    mode: "transition",
    wave: script.waves.length,
    bruteIndex: script.brutes.length,
    messageKey: "raid.msg.assaultClear",
  });
}

function tickRetreatAllies(state: RaidBattleState, dt: number): void {
  const goals = state.encounter.formationGoals;
  if (!goals) return;
  for (const g of goals) {
    const u = state.units.find((x) => x.id === g.unitId && x.hp > 0);
    if (!u) continue;
    const dx = g.x - u.x;
    const dy = g.y - u.y;
    const len = Math.hypot(dx, dy);
    if (len < 2) {
      u.x = g.x;
      u.y = g.y;
      u.vx = 0;
      u.vy = 0;
      u.intentVx = 0;
      u.intentVy = 0;
      continue;
    }
    const step = Math.min(len, u.speed * dt);
    u.x += (dx / len) * step;
    u.y += (dy / len) * step;
    u.vx = (dx / len) * u.speed;
    u.vy = (dy / len) * u.speed;
    u.intentVx = u.vx;
    u.intentVy = u.vy;
    u.brain.targetId = -1;
  }
}

function enterDropPhase(
  state: RaidBattleState,
  script: AssaultScript,
  fns: AssaultSpawnFns,
): void {
  const map = raidCatalog.balance.map;
  const spawn = fns.clampToPlayBounds(
    map.bossX,
    map.bossY,
    state.boss.radius,
  );
  state.boss.x = spawn.x;
  state.boss.y = spawn.y;
  state.boss.vx = 0;
  state.boss.vy = 0;
  state.boss.intentVx = 0;
  state.boss.intentVy = 0;
  state.boss.telegraph = null;
  state.boss.leap = null;
  // Stay unarmed until drop/hold completes.
  state.boss.stunnedUntil = Number.POSITIVE_INFINITY;
  state.encounter.transitionPhase = "drop";
  state.encounter.transitionRemain = script.transition.dropSec;
  state.encounter.formationGoals = null;
  push(state, {
    kind: RaidEvent.Encounter,
    mode: "transition",
    wave: script.waves.length,
    bruteIndex: script.brutes.length,
    messageKey: "raid.msg.bossDrop",
  });
}

function enterHoldPhase(state: RaidBattleState, script: AssaultScript): void {
  state.encounter.transitionPhase = "hold";
  state.encounter.transitionRemain = script.transition.holdSec;
}

function armBossCombat(state: RaidBattleState, script: AssaultScript): void {
  state.boss.stunnedUntil = 0;
  state.boss.telegraph = null;
  state.boss.leap = null;
  state.encounter.mode = "boss";
  state.encounter.transitionPhase = null;
  state.encounter.transitionRemain = 0;
  state.encounter.bossCombatArmed = true;
  state.encounter.formationGoals = null;
  // Colossus stage clock — independent of W3/brute escalate.
  beginEscalateScope(state);
  push(state, {
    kind: RaidEvent.Encounter,
    mode: "boss",
    wave: script.waves.length,
    bruteIndex: script.brutes.length,
    messageKey: "raid.msg.bossArrive",
  });
}

function tickTransition(
  state: RaidBattleState,
  script: AssaultScript,
  dt: number,
  fns: AssaultSpawnFns,
): void {
  const phase = state.encounter.transitionPhase;
  if (phase === "retreat") {
    tickRetreatAllies(state, dt);
    state.encounter.transitionRemain = Math.max(
      0,
      state.encounter.transitionRemain - dt,
    );
    if (state.encounter.transitionRemain <= 1e-6) {
      enterDropPhase(state, script, fns);
    }
    return;
  }
  if (phase === "drop") {
    state.encounter.transitionRemain = Math.max(
      0,
      state.encounter.transitionRemain - dt,
    );
    if (state.encounter.transitionRemain <= 1e-6) {
      enterHoldPhase(state, script);
    }
    return;
  }
  if (phase === "hold") {
    state.encounter.transitionRemain = Math.max(
      0,
      state.encounter.transitionRemain - dt,
    );
    if (state.encounter.transitionRemain <= 1e-6) {
      armBossCombat(state, script);
    }
  }
}

/**
 * Advance assault director. Call from tickRaid when mode is assault|transition
 * (or boss without combat armed).
 */
export function tickAssault(
  state: RaidBattleState,
  dt: number,
  fns: AssaultSpawnFns,
): void {
  if (state.encounter.mode === "boss" && state.encounter.bossCombatArmed) {
    return;
  }
  const script = assaultScript(state.encounter.scriptId);

  if (state.encounter.mode === "transition") {
    tickTransition(state, script, dt, fns);
    return;
  }

  if (state.encounter.mode !== "assault") return;

  state.encounter.addBarkCdLeft = Math.max(
    0,
    state.encounter.addBarkCdLeft - dt,
  );

  if (state.encounter.delayRemain > 1e-6) {
    state.encounter.delayRemain -= dt;
    return;
  }

  const waveIdx = state.encounter.waveIndex;
  const wave = script.waves[waveIdx];

  if (wave && !state.encounter.waveSpawned) {
    beginWaveDrip(state, script, waveIdx);
    // Fall through so the first chunk can fire this tick.
  }

  if (wave && state.encounter.waveSpawned && state.encounter.toSpawn > 0) {
    tryMidDripChargers(state, script, waveIdx, fns);
    tickWaveDrip(state, script, waveIdx, dt, fns);
    // Mid-wave combat bark only while thralls are on the field (after drip
    // continues or while waiting); still try during drip for early chatter.
    tryEmitAddCombatBark(state);
    return;
  }

  // Catch mid-drip gate if the last chunk finished the stream past atDripFrac.
  if (wave && state.encounter.waveSpawned) {
    tryMidDripChargers(state, script, waveIdx, fns);
  }

  // Occasional combat barks while thralls fight (post-drip / pre-brute).
  tryEmitAddCombatBark(state);

  // Stream done. Gate pre-elite swarm vs elites separately so a stray thrall
  // (or unkillable drop stuck on a sibling) cannot softlock the director:
  //   drip clear → thralls+mid-drip chargers dead → spawn brute pack
  //   → elites(+any leftover chargers) dead → next/transition
  // Mid-drip chargers must NOT coexist with the brute pack (assault.json note).
  const needsBrute =
    bruteCountForWave(script, waveIdx) > 0 &&
    !state.encounter.bruteSpawnedForWave;

  if (needsBrute) {
    if (livingPreBruteGate(state).length > 0) return;
    spawnBruteForWave(state, script, fns);
    return;
  }

  // Elites already spawned (or none for this wave): wait on brutes + chargers.
  if (state.encounter.bruteSpawnedForWave && livingBrutes(state).length > 0) {
    return;
  }
  if (
    state.encounter.chargerSpawnedForWave &&
    livingChargers(state).length > 0
  ) {
    return;
  }

  // No brutes for this wave: still clear leftover thralls before advancing.
  if (
    !state.encounter.bruteSpawnedForWave &&
    livingThralls(state).length > 0
  ) {
    return;
  }

  const next = waveIdx + 1;
  if (next < script.waves.length) {
    state.encounter.waveIndex = next;
    state.encounter.waveSpawned = false;
    state.encounter.toSpawn = 0;
    state.encounter.toSpawnSouth = 0;
    state.encounter.nextChunkIn = 0;
    state.encounter.bruteSpawnedForWave = false;
    state.encounter.chargerMidDripFiredMask = 0;
    state.encounter.chargerSpawnedForWave = false;
    state.encounter.delayRemain = script.waves[next]?.delaySec ?? 0;
    // Between stages: no stacks until next beginWaveDrip.
    clearEscalateScope(state);
    return;
  }

  beginTransition(state, script, fns);
}
